/**
 * Brokerages Data Facade
 * All brokerage data access goes through this interface.
 * Following the playbook: data/* facades only, no direct DB access from UI.
 */

import { supabase } from '@/integrations/supabase/client';
import type { 
  Brokerage, 
  BrokerageCard, 
  BrokerageLocation, 
  BrokerageFilters,
  BrokerageLeadInput 
} from '@/contracts/brokerage';

// Helper to calculate distance between two points (Haversine formula)
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Get filtered list of brokerages for browse page
 */
export async function getBrokerages(filters: BrokerageFilters): Promise<{
  brokerages: BrokerageCard[];
  total: number;
}> {
  let query = supabase
    .from('brokerages')
    .select('*', { count: 'exact' })
    .eq('is_published', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  // Apply filters
  if (filters.brokerage_size?.length) {
    query = query.in('brokerage_size', filters.brokerage_size);
  }
  
  if (filters.brokerage_type?.length) {
    // Cast to the enum type expected by Supabase
    query = query.in('brokerage_type', filters.brokerage_type as ('physical' | 'hybrid' | 'virtual')[]);
  }
  
  if (filters.support_level?.length) {
    query = query.in('support_level', filters.support_level);
  }
  
  if (filters.search) {
    query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
  }

  // Apply best_for filter (JSONB array containment)
  if (filters.best_for?.length) {
    // Filter brokerages that have ANY of the selected best_for values
    query = query.overlaps('best_for', filters.best_for);
  }

  // Apply priorities filter (JSONB array containment)
  if (filters.priorities?.length) {
    query = query.overlaps('priorities', filters.priorities);
  }

  // Pagination
  query = query.range(filters.offset || 0, (filters.offset || 0) + (filters.limit || 24) - 1);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching brokerages:', error);
    throw error;
  }

  // If state filter is set, get location counts per brokerage
  let locationCounts: Record<string, number> = {};
  if (filters.state && data?.length) {
    const brokerageIds = data.map(b => b.id);
    const { data: locations } = await supabase
      .from('brokerage_locations')
      .select('brokerage_id')
      .in('brokerage_id', brokerageIds)
      .eq('state', filters.state)
      .eq('is_active', true);
    
    if (locations) {
      locations.forEach(loc => {
        locationCounts[loc.brokerage_id] = (locationCounts[loc.brokerage_id] || 0) + 1;
      });
    }
  }

  const brokerages: BrokerageCard[] = (data || []).map(b => ({
    id: b.id,
    slug: b.slug,
    name: b.name,
    logo: b.logo,
    description: b.description,
    coverage_type: b.coverage_type,
    brokerage_size: b.brokerage_size,
    brokerage_type: b.brokerage_type,
    support_level: b.support_level,
    best_for: Array.isArray(b.best_for) ? (b.best_for as string[]) : [],
    is_published: b.is_published ?? false,
    locations_in_state: locationCounts[b.id] || 0,
    // Financial snapshot
    split_starting_percent: b.split_starting_percent ? Number(b.split_starting_percent) : null,
    split_max_percent: b.split_max_percent ? Number(b.split_max_percent) : null,
    cap_amount: b.cap_amount ? Number(b.cap_amount) : null,
    monthly_fee: b.monthly_fee ? Number(b.monthly_fee) : null,
    transaction_fee: b.transaction_fee ? Number(b.transaction_fee) : null,
    sign_on_bonus_max: b.sign_on_bonus_max ? Number(b.sign_on_bonus_max) : null,
    has_revenue_share: b.has_revenue_share ?? false,
    has_stock_awards: b.has_stock_awards ?? false,
  }));

  return {
    brokerages,
    total: count || 0,
  };
}

/**
 * Get single brokerage by slug with all details
 */
export async function getBrokerage(slug: string): Promise<Brokerage | null> {
  const { data, error } = await supabase
    .from('brokerages')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    console.error('Error fetching brokerage:', error);
    throw error;
  }

  return {
    ...data,
    operating_states: Array.isArray(data.operating_states) ? (data.operating_states as string[]) : [],
    best_for: Array.isArray(data.best_for) ? (data.best_for as string[]) : [],
    priorities: Array.isArray(data.priorities) ? (data.priorities as string[]) : [],
    // Media fields
    gallery: Array.isArray(data.gallery) ? (data.gallery as string[]) : [],
    testimonial_videos: Array.isArray(data.testimonial_videos) ? data.testimonial_videos : [],
    feature_sections: Array.isArray(data.feature_sections) ? data.feature_sections : [],
    // Parse numeric financial fields
    split_starting_percent: data.split_starting_percent ? Number(data.split_starting_percent) : null,
    split_max_percent: data.split_max_percent ? Number(data.split_max_percent) : null,
    cap_amount: data.cap_amount ? Number(data.cap_amount) : null,
    monthly_fee: data.monthly_fee ? Number(data.monthly_fee) : null,
    technology_fee: data.technology_fee ? Number(data.technology_fee) : null,
    eo_fee: data.eo_fee ? Number(data.eo_fee) : null,
    marketing_fee: data.marketing_fee ? Number(data.marketing_fee) : null,
    royalty_fee_percent: data.royalty_fee_percent ? Number(data.royalty_fee_percent) : null,
    transaction_fee: data.transaction_fee ? Number(data.transaction_fee) : null,
    sign_on_bonus_min: data.sign_on_bonus_min ? Number(data.sign_on_bonus_min) : null,
    sign_on_bonus_max: data.sign_on_bonus_max ? Number(data.sign_on_bonus_max) : null,
  } as Brokerage;
}

/**
 * Get locations for a brokerage, optionally filtered by state and sorted by distance
 */
export async function getBrokerageLocations(
  brokerageId: string,
  options?: {
    state?: string;
    lat?: number;
    lng?: number;
    limit?: number;
  }
): Promise<BrokerageLocation[]> {
  let query = supabase
    .from('brokerage_locations')
    .select('*')
    .eq('brokerage_id', brokerageId)
    .eq('is_active', true)
    .order('city', { ascending: true });

  if (options?.state) {
    // Include virtual locations (they bypass state filter) OR locations in the state
    query = query.or(`state.eq.${options.state},is_virtual.eq.true`);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching brokerage locations:', error);
    throw error;
  }

  let locations: BrokerageLocation[] = (data || []).map(loc => ({
    ...loc,
    lat: loc.lat ? Number(loc.lat) : null,
    lng: loc.lng ? Number(loc.lng) : null,
    distance_miles: undefined,
  }));

  // Calculate distances if user coordinates provided
  if (options?.lat && options?.lng) {
    locations = locations.map(loc => {
      if (loc.lat && loc.lng) {
        return {
          ...loc,
          distance_miles: calculateDistance(options.lat!, options.lng!, loc.lat, loc.lng),
        };
      }
      return loc;
    });

    // Sort by distance (virtual locations last if no coordinates)
    locations.sort((a, b) => {
      if (a.distance_miles === undefined && b.distance_miles === undefined) return 0;
      if (a.distance_miles === undefined) return 1;
      if (b.distance_miles === undefined) return -1;
      return a.distance_miles - b.distance_miles;
    });
  }

  return locations;
}

/**
 * Submit a lead (agent interested in a brokerage)
 */
export async function submitBrokerageLead(lead: BrokerageLeadInput): Promise<{ id: string }> {
  // Get current user if logged in
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('brokerage_leads')
    .insert({
      brokerage_id: lead.brokerage_id,
      location_id: lead.location_id || null,
      agent_name: lead.agent_name,
      agent_email: lead.agent_email,
      agent_phone: lead.agent_phone || null,
      message: lead.message || null,
      agent_city: lead.agent_city || null,
      agent_state: lead.agent_state || null,
      user_id: user?.id || null,
    })
    .select('id')
    .single();

  if (error) {
    console.error('Error submitting brokerage lead:', error);
    throw error;
  }

  return { id: data.id };
}

/**
 * Get all brokerages for admin
 */
export async function getAdminBrokerages(): Promise<Brokerage[]> {
  const { data, error } = await supabase
    .from('brokerages')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching admin brokerages:', error);
    throw error;
  }

  return (data || []).map(b => ({
    ...b,
    operating_states: Array.isArray(b.operating_states) ? (b.operating_states as string[]) : [],
    best_for: Array.isArray(b.best_for) ? (b.best_for as string[]) : [],
    priorities: Array.isArray(b.priorities) ? (b.priorities as string[]) : [],
  })) as Brokerage[];
}

/**
 * Create a new brokerage (admin only)
 */
export async function createBrokerage(brokerage: Partial<Brokerage>): Promise<Brokerage> {
  const { data, error } = await supabase
    .from('brokerages')
    .insert({
      slug: brokerage.slug!,
      name: brokerage.name!,
      logo: brokerage.logo || null,
      description: brokerage.description || null,
      coverage_type: brokerage.coverage_type || null,
      operating_states: brokerage.operating_states || [],
      brokerage_size: brokerage.brokerage_size || null,
      brokerage_type: brokerage.brokerage_type || null,
      support_level: brokerage.support_level || null,
      best_for: brokerage.best_for || [],
      priorities: brokerage.priorities || [],
      training_availability: brokerage.training_availability || null,
      mentorship_structure: brokerage.mentorship_structure || null,
      compensation_model: brokerage.compensation_model || null,
      tech_provided: brokerage.tech_provided || null,
      lead_programs: brokerage.lead_programs || null,
      culture_independent_vs_collaborative: brokerage.culture_independent_vs_collaborative || null,
      culture_traditional_vs_innovative: brokerage.culture_traditional_vs_innovative || null,
      what_makes_us_different: brokerage.what_makes_us_different || null,
      ideal_agent_description: brokerage.ideal_agent_description || null,
      website_url: brokerage.website_url || null,
      sort_order: brokerage.sort_order || 0,
      is_published: brokerage.is_published || false,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating brokerage:', error);
    throw error;
  }

  return {
    ...data,
    operating_states: Array.isArray(data.operating_states) ? (data.operating_states as string[]) : [],
    best_for: Array.isArray(data.best_for) ? (data.best_for as string[]) : [],
    priorities: Array.isArray(data.priorities) ? (data.priorities as string[]) : [],
  } as Brokerage;
}

/**
 * Update a brokerage
 */
export async function updateBrokerage(id: string, updates: Partial<Brokerage>): Promise<Brokerage> {
  // Build update object with only defined values
  const updateObj: Record<string, unknown> = {};
  
  if (updates.slug !== undefined) updateObj.slug = updates.slug;
  if (updates.name !== undefined) updateObj.name = updates.name;
  if (updates.logo !== undefined) updateObj.logo = updates.logo;
  if (updates.description !== undefined) updateObj.description = updates.description;
  if (updates.coverage_type !== undefined) updateObj.coverage_type = updates.coverage_type;
  if (updates.operating_states !== undefined) updateObj.operating_states = updates.operating_states;
  if (updates.brokerage_size !== undefined) updateObj.brokerage_size = updates.brokerage_size;
  if (updates.brokerage_type !== undefined) updateObj.brokerage_type = updates.brokerage_type;
  if (updates.support_level !== undefined) updateObj.support_level = updates.support_level;
  if (updates.best_for !== undefined) updateObj.best_for = updates.best_for;
  if (updates.priorities !== undefined) updateObj.priorities = updates.priorities;
  if (updates.training_availability !== undefined) updateObj.training_availability = updates.training_availability;
  if (updates.mentorship_structure !== undefined) updateObj.mentorship_structure = updates.mentorship_structure;
  if (updates.compensation_model !== undefined) updateObj.compensation_model = updates.compensation_model;
  if (updates.tech_provided !== undefined) updateObj.tech_provided = updates.tech_provided;
  if (updates.lead_programs !== undefined) updateObj.lead_programs = updates.lead_programs;
  if (updates.culture_independent_vs_collaborative !== undefined) updateObj.culture_independent_vs_collaborative = updates.culture_independent_vs_collaborative;
  if (updates.culture_traditional_vs_innovative !== undefined) updateObj.culture_traditional_vs_innovative = updates.culture_traditional_vs_innovative;
  if (updates.what_makes_us_different !== undefined) updateObj.what_makes_us_different = updates.what_makes_us_different;
  if (updates.ideal_agent_description !== undefined) updateObj.ideal_agent_description = updates.ideal_agent_description;
  if (updates.website_url !== undefined) updateObj.website_url = updates.website_url;
  if (updates.sort_order !== undefined) updateObj.sort_order = updates.sort_order;
  if (updates.is_published !== undefined) updateObj.is_published = updates.is_published;

  const { data, error } = await supabase
    .from('brokerages')
    .update(updateObj)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating brokerage:', error);
    throw error;
  }

  return {
    ...data,
    operating_states: Array.isArray(data.operating_states) ? (data.operating_states as string[]) : [],
    best_for: Array.isArray(data.best_for) ? (data.best_for as string[]) : [],
    priorities: Array.isArray(data.priorities) ? (data.priorities as string[]) : [],
  } as Brokerage;
}

/**
 * Create a brokerage location
 */
export async function createBrokerageLocation(location: Partial<BrokerageLocation>): Promise<BrokerageLocation> {
  const { data, error } = await supabase
    .from('brokerage_locations')
    .insert({
      brokerage_id: location.brokerage_id!,
      name: location.name!,
      city: location.city!,
      state: location.state!,
      address: location.address || null,
      zip_code: location.zip_code || null,
      lat: location.lat || null,
      lng: location.lng || null,
      is_virtual: location.is_virtual || false,
      recruiter_name: location.recruiter_name || null,
      recruiter_email: location.recruiter_email || null,
      recruiter_phone: location.recruiter_phone || null,
      booking_url: location.booking_url || null,
      location_pitch: location.location_pitch || null,
      is_active: location.is_active ?? true,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating brokerage location:', error);
    throw error;
  }

  return {
    ...data,
    lat: data.lat ? Number(data.lat) : null,
    lng: data.lng ? Number(data.lng) : null,
  } as BrokerageLocation;
}

/**
 * Update a brokerage location
 */
export async function updateBrokerageLocation(id: string, updates: Partial<BrokerageLocation>): Promise<BrokerageLocation> {
  const updateObj: Record<string, unknown> = {};
  
  if (updates.name !== undefined) updateObj.name = updates.name;
  if (updates.city !== undefined) updateObj.city = updates.city;
  if (updates.state !== undefined) updateObj.state = updates.state;
  if (updates.address !== undefined) updateObj.address = updates.address;
  if (updates.zip_code !== undefined) updateObj.zip_code = updates.zip_code;
  if (updates.lat !== undefined) updateObj.lat = updates.lat;
  if (updates.lng !== undefined) updateObj.lng = updates.lng;
  if (updates.is_virtual !== undefined) updateObj.is_virtual = updates.is_virtual;
  if (updates.recruiter_name !== undefined) updateObj.recruiter_name = updates.recruiter_name;
  if (updates.recruiter_email !== undefined) updateObj.recruiter_email = updates.recruiter_email;
  if (updates.recruiter_phone !== undefined) updateObj.recruiter_phone = updates.recruiter_phone;
  if (updates.booking_url !== undefined) updateObj.booking_url = updates.booking_url;
  if (updates.location_pitch !== undefined) updateObj.location_pitch = updates.location_pitch;
  if (updates.is_active !== undefined) updateObj.is_active = updates.is_active;

  const { data, error } = await supabase
    .from('brokerage_locations')
    .update(updateObj)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating brokerage location:', error);
    throw error;
  }

  return {
    ...data,
    lat: data.lat ? Number(data.lat) : null,
    lng: data.lng ? Number(data.lng) : null,
  } as BrokerageLocation;
}

/**
 * Delete a brokerage location
 */
export async function deleteBrokerageLocation(id: string): Promise<void> {
  const { error } = await supabase
    .from('brokerage_locations')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting brokerage location:', error);
    throw error;
  }
}
